import type { XmaxClientOptions } from '../client/types'
import { apiHost, telemetryEnvironment, telemetryReportSource } from '../telemetry/collector'
import { beginStartup, diagnosticError, disconnectReasonForStage, STARTUP_TRACE, type WithStartupTrace } from '../telemetry/startup'
import { safeMd5Hex } from '../telemetry/md5'
import { XMAX_OPEN_API_PRODUCTION_BASE_URL } from '../api/client'
import { preflightCameraAccess } from '../rtc/media/camera-preflight'
import { createMediaFileStream, isImageMediaFile } from '../rtc/media/media-file-stream'
import { createSDKI18n } from '../i18n'
import { notifyError } from '../shared/error-utils'
import { XmaxSdkError, attachPublicErrorDetails, toXmaxSdkError } from '../shared/sdk-error'
import { isInstantPrompt } from './instant'
import { findUploadedAsset, replayUploadedAssetEvents } from '../telemetry/assets'
import {
  RealtimeSessionImpl,
  type PreparedRealtimeMedia,
  type RealtimeSession,
  withPreparedRealtimeMedia,
} from './session'
import { resolveCameraRealtimeStreamSetting } from './camera'
import { DEFAULT_REALTIME_PLAYBACK_SETTING } from './default'
import { readMediaStreamSourceSetting } from './utils'
import type {
  RealtimeConnectCameraOptions,
  RealtimeConnectMediaOptions,
  RealtimeConnectOptions,
} from './types'

function getFirstLiveAudioTrack(stream: MediaStream): MediaStreamTrack | undefined {
  return stream.getAudioTracks().find((track) => track.readyState === 'live')
}

/**
 * @brief 实时生成能力入口。
 * 提供摄像头连接、媒体文件连接和自有 MediaStream 连接三种接入方式。
 */
export type RealtimeClient = {

  /**
   * @brief 采集浏览器摄像头并建立实时会话。
   * @param options 摄像头采集、RTC 编码、渲染和生成设置。
   * @returns 已建立的实时会话；SDK 拥有并负责释放摄像头媒体。
   */
  connectCamera: (options: RealtimeConnectCameraOptions) => Promise<RealtimeSession>

  /**
   * @brief 将图片、视频 Blob 或远程 URL 作为输入建立实时会话。
   * @param source 媒体 Blob（File 继承自 Blob）或可访问的媒体 URL。
   * @param options 媒体播放、RTC 编码、渲染和生成设置。
   * @returns 已建立的实时会话；SDK 负责释放内部创建的媒体资源。
   */
  connectMedia: (
    source: Blob | string,
    options: RealtimeConnectMediaOptions,
  ) => Promise<RealtimeSession>

  /**
   * @brief 使用调用方已有的 MediaStream 建立实时会话。
   * @param stream 至少包含一条可用视频轨道的 MediaStream。
   * @param options RTC 编码、渲染和生成设置。
   * @returns 已建立的实时会话。
   * @notes MediaStream 的所有权仍属于调用方；disconnect 不会停止其中的 MediaStreamTrack。
   */
  connect: (stream: MediaStream, options: RealtimeConnectOptions) => Promise<RealtimeSession>
}

const CONNECT_PREPARED_REALTIME_MEDIA = Symbol('connectPreparedRealtimeMedia')

type InternalRealtimeClient = RealtimeClient & {
  [CONNECT_PREPARED_REALTIME_MEDIA]: (
    prepared: PreparedRealtimeMedia,
    options: RealtimeConnectOptions,
  ) => Promise<RealtimeSession>
}

/** @internal Preserve file-media ownership and kind when Playground already built the stream. */
export function connectPreparedRealtimeMediaInternal(
  client: RealtimeClient,
  prepared: PreparedRealtimeMedia,
  options: RealtimeConnectOptions,
): Promise<RealtimeSession> {
  const connectPrepared = (client as InternalRealtimeClient)[CONNECT_PREPARED_REALTIME_MEDIA]
  if (typeof connectPrepared !== 'function') {
    throw new XmaxSdkError('INVALID_INPUT', 'Expected an SDK RealtimeClient')
  }
  return connectPrepared(prepared, options)
}

/** @internal Create the realtime capability exposed by {@link XmaxClient.realtime}. */
export function createRealtimeClient(clientOptions: XmaxClientOptions): RealtimeClient {
  const i18n = clientOptions.i18n ?? createSDKI18n({ locale: clientOptions.locale })
  const telemetryBaseUrl = clientOptions.baseUrl ?? XMAX_OPEN_API_PRODUCTION_BASE_URL
  const apiKeyMd5 = clientOptions.telemetry === false
    ? undefined
    : safeMd5Hex(clientOptions.apiKey ?? clientOptions.authToken ?? '')

  const realtimeClient: InternalRealtimeClient = {
    [CONNECT_PREPARED_REALTIME_MEDIA]: async (
      prepared: PreparedRealtimeMedia,
      connectOptions: RealtimeConnectOptions,
    ) => RealtimeSessionImpl.connect(
      prepared.stream,
      withPreparedRealtimeMedia(connectOptions, prepared),
      clientOptions,
    ),
    connectCamera: async (connectOptions: RealtimeConnectCameraOptions) => {
      const trace = (connectOptions as WithStartupTrace)[STARTUP_TRACE]
      try {
        const streamSetting = resolveCameraRealtimeStreamSetting(connectOptions)
        const captureStartedAt = performance.now()
        trace?.enter('camera_capture')
        trace?.record('sdk_capture_get_user_media_start', {
          requested_width: streamSetting.width,
          requested_height: streamSetting.height,
          requested_fps: streamSetting.fps,
          facing_mode: connectOptions.facingMode,
        })
        try {
          await preflightCameraAccess({
            facingMode: connectOptions.facingMode,
            width: streamSetting.width,
            height: streamSetting.height,
            frameRate: streamSetting.fps,
          })
          trace?.record('sdk_capture_get_user_media_end', {
            ok: true,
            duration_ms: Math.round(performance.now() - captureStartedAt),
          })
          trace?.enter('media_prepare')
        } catch (error) {
          trace?.record('sdk_capture_get_user_media_end', {
            ok: false,
            duration_ms: Math.round(performance.now() - captureStartedAt),
            ...diagnosticError(error),
          })
          throw error
        }
        const stream = new MediaStream()
        const withAudio = connectOptions.audio?.publish !== false
        const preparedOptions = withPreparedRealtimeMedia({
          ...connectOptions,
          stream: streamSetting,
        }, {
          kind: 'camera',
          stream,
          // Replaced with the actual settings returned by startVideoCapture during bootstrap.
          sourceSetting: streamSetting,
          internalCamera: {
            facingMode: connectOptions.facingMode,
            withAudio,
            // Camera defaults to the front-facing device when facingMode is omitted.
            mirrorInput: connectOptions.facingMode !== 'environment',
          },
          owned: true,
        })
        return await RealtimeSessionImpl.connect(stream, preparedOptions, clientOptions)
      } catch (error) {
        const resolved = toXmaxSdkError(error, { code: 'CAMERA_ERROR', message: 'Failed to connect RTC camera' })
        attachPublicErrorDetails(resolved, { reason_code: 'camera_capture_failed', failure_stage: 'camera_capture', trace_id: trace?.traceId })
        throw notifyError(
          resolved,
          connectOptions.onError ?? clientOptions.onError,
          {
            code: 'CAMERA_ERROR',
            message: 'Failed to connect RTC camera',
          },
        )
      }
    },
    connectMedia: async (source: Blob | string, connectOptions: RealtimeConnectMediaOptions) => {
      let mediaStream: Awaited<ReturnType<typeof createMediaFileStream>> | null = null
      try {
        const kind = isImageMediaFile(source) ? 'image' : 'video'
        mediaStream = await createMediaFileStream(source, {
          i18n,
          playbackRate: connectOptions.playback?.playbackRate
            ?? DEFAULT_REALTIME_PLAYBACK_SETTING.playbackRate,
          backgroundColor: connectOptions.render?.backgroundColor,
          poster: kind === 'image' && isInstantPrompt(connectOptions.context?.prompt ?? '')
            ? { type: 'image/jpeg', quality: 0.9 }
            : undefined,
        })
        const prepared = mediaStream
        const preparedOptions = withPreparedRealtimeMedia(connectOptions, {
          kind,
          posterUrl: prepared.posterUrl,
          stream: prepared.previewStream,
          publishTrack: prepared.videoTrack,
          publishAudioTrack: connectOptions.audio?.publish !== false
            ? prepared.audioTrack
            : undefined,
          sourceSetting: {
            width: prepared.sourceWidth,
            height: prepared.sourceHeight,
            fps: prepared.fps,
          },
          owned: true,
          cleanup: () => prepared.destroy(),
          appleMobileVideoFileCompatibility: kind === 'video' && typeof source !== 'string',
        })
        return await RealtimeSessionImpl.connect(prepared.previewStream, preparedOptions, clientOptions)
      } catch (error) {
        mediaStream?.destroy()
        const resolved = toXmaxSdkError(error, { code: 'MEDIA_PROCESSING_ERROR', message: 'Failed to connect media' })
        attachPublicErrorDetails(resolved, { reason_code: 'media_prepare_failed', failure_stage: 'media_prepare',
          trace_id: (connectOptions as WithStartupTrace)[STARTUP_TRACE]?.traceId })
        throw notifyError(
          resolved,
          connectOptions.onError ?? clientOptions.onError,
          {
            code: 'MEDIA_PROCESSING_ERROR',
            message: 'Failed to connect media',
          },
        )
      }
    },
    connect: async (stream: MediaStream, connectOptions: RealtimeConnectOptions) => {
      const publishTrack = stream.getVideoTracks()[0]
      const sourceSetting = publishTrack
        ? await readMediaStreamSourceSetting(publishTrack)
        : undefined
      const preparedOptions = withPreparedRealtimeMedia(connectOptions, {
        kind: 'stream',
        stream,
        publishTrack,
        publishAudioTrack: connectOptions.audio?.publish !== false
          ? getFirstLiveAudioTrack(stream)
          : undefined,
        sourceSetting,
        owned: false,
      })
      return RealtimeSessionImpl.connect(stream, preparedOptions, clientOptions)
    },
  }

  async function observe<T extends RealtimeConnectOptions>(
    kind: string,
    options: T,
    operation: (options: T) => Promise<RealtimeSession>,
    mediaBytes?: number,
  ): Promise<RealtimeSession> {
    const uploadedAsset = findUploadedAsset(options.context?.refImageUrl)
    const reportSource = telemetryReportSource(clientOptions.reportSource)
    const trace = beginStartup(clientOptions.telemetry, {
      media_kind: kind,
      media_bytes: mediaBytes,
      api_host: apiHost(telemetryBaseUrl),
      api_key_md5: apiKeyMd5,
      env: telemetryEnvironment(reportSource),
      report_source: reportSource,
      model: options.model.name,
      auto_start: options.autoStart !== false,
      entry_point: 'connect',
      asset_operation_id: uploadedAsset?.operationId,
      asset_age_ms: uploadedAsset ? Math.max(0, performance.now() - uploadedAsset.completedAtMonotonicMs) : undefined,
    }, clientOptions.probe)
    if (trace && uploadedAsset) replayUploadedAssetEvents(trace, uploadedAsset)
    try { return await operation(Object.assign({}, options, { [STARTUP_TRACE]: trace })) }
    catch (error) {
      trace?.finish('error', {
        failure_reason: disconnectReasonForStage(trace.currentStage),
        failure_stage: trace.currentStage,
        ...diagnosticError(error),
      })
      throw error
    }
  }

  const observed: InternalRealtimeClient = {
    [CONNECT_PREPARED_REALTIME_MEDIA]: (prepared, options) => observe(prepared.kind, options, value => realtimeClient[CONNECT_PREPARED_REALTIME_MEDIA](prepared, value)),
    connectCamera: options => observe('camera', options, value => realtimeClient.connectCamera(value)),
    connectMedia: (source, options) => observe(isImageMediaFile(source) ? 'image' : 'video', options, value => realtimeClient.connectMedia(source, value), typeof source === 'string' ? undefined : source.size),
    connect: (stream, options) => observe('stream', options, value => realtimeClient.connect(stream, value)),
  }
  return observed
}
